/**
 * AddClientModal Component
 * Allows advisors/managers to add new clients with plan selection (Plus Free / PRO+ Paid)
 * Free clients: unlimited, 1-year validity
 * Paid clients: limited to 50 (or current limit), requires top-up after
 */
import React, { useState } from 'react';
import { FaTimes, FaUser, FaEnvelope, FaPhone, FaSpinner, FaCheck, FaCopy, FaExclamationTriangle, FaCrown, FaGift, FaCheckCircle, FaLock, FaInfoCircle } from 'react-icons/fa';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AddClientModal = ({ isOpen, onClose, onSuccess, clientLimitInfo }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    plan: 'plus'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);
  const [copiedField, setCopiedField] = useState(null);

  if (!isOpen) return null;

  const paidClientsCount = clientLimitInfo?.paid_clients_count || 0;
  const paidLimit = clientLimitInfo?.current_limit || 50;
  const freeClientsCount = clientLimitInfo?.free_clients_count || 0;
  const paidLimitReached = paidClientsCount >= paidLimit;

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleCopy = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/advisor/add-client`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        plan: formData.plan
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSuccess(response.data);
      if (onSuccess) onSuccess();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to add client');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({ name: '', email: '', phone: '', plan: 'plus' });
    setError('');
    setSuccess(null);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="add-client-modal">
      <div className="bg-gradient-to-br from-[#1A2942] to-[#0F172A] rounded-2xl border border-[#2D5A8F] shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-[#2D5A8F] flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-white">{success ? 'Client Created' : 'Add New Client'}</h2>
            <p className="text-gray-400 text-sm mt-1">
              {success ? 'Share credentials with your client' : 'Create a client account'}
            </p>
          </div>
          <button onClick={handleClose} className="text-gray-400 hover:text-white transition-colors" data-testid="close-modal-btn">
            <FaTimes size={18} />
          </button>
        </div>

        {/* Success State */}
        {success ? (
          <div className="p-6 space-y-4">
            <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 text-center">
              <FaCheck className="text-green-400 text-2xl mx-auto mb-2" />
              <p className="text-green-400 font-medium">{success.message}</p>
            </div>

            <div className="bg-[#0F172A] rounded-xl p-4 space-y-3 border border-[#2D5A8F]">
              <p className="text-gray-400 text-xs uppercase font-medium tracking-wider">Login Credentials</p>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-xs">Email</p>
                  <p className="text-white font-medium">{success.credentials?.email}</p>
                </div>
                <button onClick={() => handleCopy(success.credentials?.email, 'email')} className="p-2 text-gray-400 hover:text-[#FFD700] transition-colors">
                  {copiedField === 'email' ? <FaCheck className="text-green-400" /> : <FaCopy />}
                </button>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-xs">Temporary Password</p>
                  <p className="text-[#FFD700] font-bold font-mono">{success.credentials?.temporary_password}</p>
                </div>
                <button onClick={() => handleCopy(success.credentials?.temporary_password, 'password')} className="p-2 text-gray-400 hover:text-[#FFD700] transition-colors">
                  {copiedField === 'password' ? <FaCheck className="text-green-400" /> : <FaCopy />}
                </button>
              </div>
              {success.client?.plan === 'plus' && success.client?.free_account_valid_until && (
                <div className="pt-2 border-t border-[#2D5A8F]">
                  <p className="text-gray-500 text-xs">Free Account Valid Until</p>
                  <p className="text-cyan-400 font-medium">{new Date(success.client.free_account_valid_until).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
                </div>
              )}
            </div>

            <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-3 flex items-start gap-2">
              <FaCheck className="text-green-400 mt-0.5 flex-shrink-0" />
              <p className="text-green-200 text-sm">
                <strong>Ready to go:</strong> A welcome email with these credentials has been sent to the client. They can log in right away — remind them to change the temporary password on first login.
              </p>
            </div>

            <button onClick={handleClose} className="w-full py-3 bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0A1929] font-bold rounded-xl hover:from-[#FFD700] hover:to-[#D4AF37] transition-all">
              Done
            </button>
          </div>
        ) : (
          /* Form State */
          <form onSubmit={handleSubmit} className="p-6 space-y-5">
            {/* Client Counts Info */}
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-3 text-center">
                <p className="text-cyan-400 text-xl font-bold">{freeClientsCount}</p>
                <p className="text-gray-400 text-xs">Free Clients</p>
                <p className="text-cyan-400/70 text-[10px]">Unlimited</p>
              </div>
              <div className={`${paidLimitReached ? 'bg-red-500/10 border-red-500/30' : 'bg-[#FFD700]/10 border-[#FFD700]/30'} border rounded-lg p-3 text-center`}>
                <p className={`text-xl font-bold ${paidLimitReached ? 'text-red-400' : 'text-[#FFD700]'}`}>{paidClientsCount}/{paidLimit}</p>
                <p className="text-gray-400 text-xs">Paid Clients</p>
                {paidLimitReached && <p className="text-red-400 text-[10px]">Limit Reached</p>}
              </div>
            </div>

            {/* Plan Selection */}
            <div data-testid="client-plan-selection">
              <label className="block text-gray-300 text-sm font-medium mb-2">Client Plan</label>
              <div className="grid grid-cols-2 gap-3">
                <button type="button" onClick={() => setFormData({ ...formData, plan: 'plus' })}
                  className={`relative p-4 rounded-xl border-2 text-left transition-all ${
                    formData.plan === 'plus' ? 'border-cyan-400 bg-cyan-500/10' : 'border-[#2D5A8F] hover:border-gray-500'
                  }`} data-testid="client-plan-plus">
                  {formData.plan === 'plus' && <FaCheckCircle className="absolute top-2 right-2 text-cyan-400" size={14} />}
                  <FaGift className="text-cyan-400 mb-1" />
                  <p className="text-white font-bold text-sm">Plus (Free)</p>
                  <p className="text-gray-400 text-xs mt-1">1-year validity</p>
                  <p className="text-cyan-400/70 text-[10px] mt-1">Unlimited</p>
                </button>
                <button type="button" onClick={() => !paidLimitReached && setFormData({ ...formData, plan: 'pro_plus' })}
                  className={`relative p-4 rounded-xl border-2 text-left transition-all ${
                    paidLimitReached ? 'border-red-500/30 opacity-50 cursor-not-allowed' :
                    formData.plan === 'pro_plus' ? 'border-[#FFD700] bg-[#FFD700]/10' : 'border-[#2D5A8F] hover:border-gray-500'
                  }`} data-testid="client-plan-pro">
                  {formData.plan === 'pro_plus' && !paidLimitReached && <FaCheckCircle className="absolute top-2 right-2 text-[#FFD700]" size={14} />}
                  {paidLimitReached && <FaLock className="absolute top-2 right-2 text-red-400" size={14} />}
                  <FaCrown className="text-[#FFD700] mb-1" />
                  <p className="text-white font-bold text-sm">PRO+ (Paid)</p>
                  <p className="text-gray-400 text-xs mt-1">₹999/year</p>
                  {paidLimitReached ? (
                    <p className="text-red-400 text-[10px] mt-1">Limit reached — buy top-up</p>
                  ) : (
                    <p className="text-[#FFD700]/70 text-[10px] mt-1">{paidLimit - paidClientsCount} slots left</p>
                  )}
                </button>
              </div>
            </div>

            {/* Free plan info notice */}
            {formData.plan === 'plus' && (
              <div className="bg-cyan-500/10 border border-cyan-500/20 rounded-lg p-3 flex items-start gap-2">
                <FaInfoCircle className="text-cyan-400 mt-0.5 flex-shrink-0" size={14} />
                <p className="text-cyan-300 text-xs">Free account valid for <strong>1 year</strong> from creation. Client must upgrade to PRO+ to continue after validity expires.</p>
              </div>
            )}

            {error && (
              <div className="p-3 bg-red-500/20 border border-red-500 rounded-lg text-red-200 text-sm">
                {error}
              </div>
            )}

            {/* Client Name */}
            <div>
              <label className="block text-gray-300 text-sm font-medium mb-2">Client Name *</label>
              <div className="relative">
                <FaUser className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type="text" name="name" value={formData.name} onChange={handleChange}
                  className="w-full pl-10 pr-4 py-3 bg-[#0F172A] border border-[#2D5A8F] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#FFD700]"
                  placeholder="Enter client's full name" required data-testid="client-name-input" />
              </div>
            </div>

            {/* Client Email */}
            <div>
              <label className="block text-gray-300 text-sm font-medium mb-2">Client Email *</label>
              <div className="relative">
                <FaEnvelope className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type="email" name="email" value={formData.email} onChange={handleChange}
                  className="w-full pl-10 pr-4 py-3 bg-[#0F172A] border border-[#2D5A8F] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#FFD700]"
                  placeholder="client@email.com" required data-testid="client-email-input" />
              </div>
            </div>

            {/* Client Phone */}
            <div>
              <label className="block text-gray-300 text-sm font-medium mb-2">Client Phone</label>
              <div className="relative">
                <FaPhone className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type="tel" name="phone" value={formData.phone} onChange={handleChange}
                  className="w-full pl-10 pr-4 py-3 bg-[#0F172A] border border-[#2D5A8F] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#FFD700]"
                  placeholder="+91 98765 43210" data-testid="client-phone-input" />
              </div>
            </div>

            {/* Submit */}
            <button type="submit" disabled={loading || (formData.plan === 'pro_plus' && paidLimitReached)}
              className="w-full py-3 bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0A1929] font-bold rounded-xl hover:from-[#FFD700] hover:to-[#D4AF37] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              data-testid="submit-client-btn">
              {loading ? (
                <><FaSpinner className="animate-spin" /> Adding Client...</>
              ) : (
                <>{formData.plan === 'pro_plus' ? <FaCrown /> : <FaGift />} Add {formData.plan === 'pro_plus' ? 'PRO+' : 'Free'} Client</>
              )}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default AddClientModal;
